import {FC, ReactNode, useEffect, useRef, useState} from 'react'
import styles from "../../components/App.module.scss";
import {TReadyAppState} from "../../types";
import {LogoHome} from "../../components/Logo";
import cn from 'classnames';
import {HomeHeader} from "../../components/SectionHeader";
import {MenuHome} from "../../components/Menu";
import {config} from "../../config";
import {getImageManifest} from "../../sagas/imageManifestLoader";
import {getImageSources} from "../../services/imageService";

const HERO_ALT = 'Palavara Pottery Studio Berlin — handmade ceramics';

const Images: FC<{filename: string, legacyUrl: string}> = ({filename, legacyUrl}) => {
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement | null>(null)
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setLoaded(true);
    }
  }, [filename]);

  // In visual test mode, show gray placeholder instead of actual images
  if (config.visualTestMode) {
    return <div
      className={styles.backgroundImg}
      style={{
        backgroundColor: '#e0e0e0',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0
      }}
      aria-hidden={true}
    />;
  }

  const sources = getImageSources(filename, getImageManifest());

  if (!sources) {
    // Manifest unavailable — fall back to legacy JPEG URL
    return <img
      loading="eager"
      {...({fetchpriority: 'high'} as any)}
      className={cn(styles.backgroundImg, {[styles.backgroundImgVisible]: loaded})}
      src={legacyUrl}
      alt={HERO_ALT}
      ref={imgRef}
      onLoad={() => setLoaded(true)}
    />;
  }

  // Build a responsive srcset using the 640/1280 variants generated by
  // scripts/generate-hero-variants.js. The base 1920w file stays as-is
  // and acts as the largest entry in the set.
  const buildSrcSet = (extension: 'avif' | 'webp' | 'jpeg') => {
    const baseUrl = sources[extension]; // e.g. https://.../home-1.avif
    const ext = extension === 'jpeg' ? 'jpg' : extension;
    const baseNoExt = baseUrl.replace(new RegExp(`\\.${ext}$`), '');
    return `${baseNoExt}-640.${ext} 640w, ${baseNoExt}-768.${ext} 768w, ${baseNoExt}-1280.${ext} 1280w, ${baseUrl} 1920w`;
  };

  return (
    <picture>
      <source srcSet={buildSrcSet('avif')} sizes="100vw" type="image/avif" />
      <source srcSet={buildSrcSet('webp')} sizes="100vw" type="image/webp" />
      <img
        loading="eager"
        {...({fetchpriority: 'high'} as any)}
        className={cn(styles.backgroundImg, {[styles.backgroundImgVisible]: loaded})}
        src={sources.jpeg}
        srcSet={buildSrcSet('jpeg')}
        sizes="100vw"
        alt={HERO_ALT}
        ref={imgRef}
        onLoad={() => setLoaded(true)}
      />
    </picture>
  );
}

const Background: FC<{children: ReactNode, filename: string, legacyUrl: string}> = ({children, filename, legacyUrl}) => {
  return <div className={styles.background}>
    <Images filename={filename} legacyUrl={legacyUrl}/>
    {children}
  </div>
}


export const Home: FC<{ state: TReadyAppState }> = ({state}) => {
  const filename = (state as any).currentImage as string;
  const legacyUrl = (state as any).url as string;

  return <Background filename={filename} legacyUrl={legacyUrl}>
    <div className={styles.header}>
      <LogoHome/>
      <h1 className={styles.title}>
        Pottery classes <br/> for kids and adults
      </h1>
      <HomeHeader state={state}/>

      <MenuHome state={state} />
    </div>
    </Background>
}
